import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import ConformityCard from '@components/card/ConformityCard'
import Icon from '@components/icon/Icons'

import useReducer from '@store/useReducer'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as LocationStore from '@store/location/location'
import * as QuestionStore from '@store/question/question'
import * as MetierStore from '@store/metier/metier'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import { filterInTree, getControleScore, scoreToRate, TEXT_COLORS } from '@screens/controle/form/utils'

const Recap = ({ form }: ControleFormProps) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const [opens, setOpens] = React.useState<string[]>([])

  const questions = useReducer(QuestionStore.store, (s) => s.controle)
  const services = useReducer(SocieteStore.store, (s) => s.services)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const user = useReducer(UserStore.store, (s) => s.user)
  const locationTree = useReducer(LocationStore.store, (s) => s.tree)
  const metiers = useReducer(MetierStore.store, (s) => s.metiers)

  const selectedLocationTree = React.useMemo(
    () => locationTree.filter((item) => filterInTree(item, form.localisationIds)),
    [form.localisationIds]
  )

  const societe = React.useMemo(() => services.find((s) => s.id === form.societeId), [services])
  const metier = React.useMemo(() => metiers.find((m) => m.id === form.metierId), [metiers])

  const manif = React.useMemo(
    () =>
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].find(
        (m) => m.id === form.manifestationId
      ),
    [manifestations]
  )

  const controleScore = getControleScore(form.reponses)
  const controleRate = scoreToRate(controleScore)

  const onOpen = (id: string, open: boolean) => {
    if (open) {
      setOpens(opens.filter((o) => o !== id))
    } else {
      setOpens([...opens, id])
    }
  }

  if (!user) {
    return null
  }

  const renderResponse = (response: ControleResponseForm, idx: number, items: ControleResponseForm[]) => {
    const question = questions.find((q) => q.id === response.questionId)

    if (!question) {
      return null
    }

    return (
      <QuestionItem key={response.questionId + response.localisationId} last={idx === items.length - 1}>
        <QuestionInfo>{i18n.t('screens.controles.form.recap.question', { count: idx + 1 })}</QuestionInfo>
        <Line>
          <QuestionIcon>
            <Icon
              name={
                response.conformite === 'CONFORME' || response.conformite === 'NON_CONCERNE' ? 'checked' : 'problem'
              }
              size={20}
              color={theme.colors.primary}
            />
          </QuestionIcon>
          <Infos>
            <Info>{question.libelle}</Info>
            <QuestionInfo color={TEXT_COLORS[response.conformite]}>
              {i18n.t(`screens.controles.form.questions.conformite.${response.conformite}`)}
            </QuestionInfo>
          </Infos>
        </Line>

        {!!response.commentaire && (
          <CommentContainer>
            <Info>{response.commentaire}</Info>
          </CommentContainer>
        )}
      </QuestionItem>
    )
  }

  const renderLocation = (item: LocationTreeItem, index: number, list: LocationTreeItem[]) => {
    const audited = form.localisationIds.includes(item.location.id)
    const responses = form.reponses.filter((r) => r.localisationId === item.location.id)

    const open = opens.includes(item.location.id)
    const subs = item.subs.filter((sub) => filterInTree(sub, form.localisationIds))

    return (
      <React.Fragment key={item.location.id}>
        <ItemContent audited={audited && open} isLast={index === list.length - 1} open={open}>
          <ItemHeader activeOpacity={0.9} onPress={() => onOpen(item.location.id, open)}>
            <ItemOpen>
              <ItemText>{item.location.libelle}</ItemText>
            </ItemOpen>

            <IconContainer>
              <Icon name={open ? 'arrow-up' : 'arrow-down'} size={20} />
            </IconContainer>
          </ItemHeader>
        </ItemContent>
        {!!open && <Subs>{audited ? responses.map(renderResponse) : subs.map(renderLocation)}</Subs>}
      </React.Fragment>
    )
  }

  const renderHeader = () => {
    if (bp === 'phone') {
      return (
        <Header>
          <Card>
            {!!societe && <Title center>{societe.nameSociete}</Title>}
            <SubTitle center>{`${i18n.t(`screens.controles.form.nature.${form.nature}`)}${
              !!manif ? ` - ${manif.libelle}` : ''
            }${metier ? ` - ${metier.libelle}` : ''}`}</SubTitle>

            <Separator />

            <Line>
              <ConformityContainer>
                <ConformityCard score={controleScore} />
              </ConformityContainer>
              <Infos>
                <ConformityContainer>
                  <Info>{i18n.t(`screens.controles.form.recap.${user.typeUtilisateur}`)}</Info>
                </ConformityContainer>
                <ConformityContainer>
                  <InfoBold color={TEXT_COLORS[controleRate.key]}>
                    {i18n.t(`screens.controles.form.questions.conformite.${controleRate.key}`)}
                  </InfoBold>
                </ConformityContainer>
              </Infos>
            </Line>
          </Card>
        </Header>
      )
    }

    return (
      <Header>
        <TitleContainer>
          <Titles>
            {!!societe && <Title>{societe.nameSociete}</Title>}
            <SubTitle>{`${i18n.t(`screens.controles.form.nature.${form.nature}`)}${
              !!manif ? ` - ${manif.libelle}` : ''
            }${metier ? ` - ${metier.libelle}` : ''}`}</SubTitle>
          </Titles>

          <ConformityContainer>
            <ConformityCard score={controleScore} />
          </ConformityContainer>
          <ConformityContainer>
            <Info>{i18n.t(`screens.controles.form.recap.${user.typeUtilisateur}`)}</Info>
          </ConformityContainer>
          <ConformityContainer>
            <InfoBold color={TEXT_COLORS[controleRate.key]}>
              {i18n.t(`screens.controles.form.questions.conformite.${controleRate.key}`)}
            </InfoBold>
          </ConformityContainer>
        </TitleContainer>
      </Header>
    )
  }

  return (
    <MainContainer>
      {renderHeader()}
      <Content>
        {!!form.commentaire && (
          <Card>
            <InfoBold>{i18n.t('screens.controles.form.recap.commentaire')}</InfoBold>
            <CommentContainer>
              <Info>{form.commentaire}</Info>
            </CommentContainer>
          </Card>
        )}

        <Card>
          <InfoBold>{i18n.t('screens.controles.form.recap.questions')}</InfoBold>
          <Locations>{selectedLocationTree.map(renderLocation)}</Locations>
        </Card>
      </Content>
    </MainContainer>
  )
}

export default Recap

const MainContainer = styled(ScrollView)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  flex: 1;
  padding-bottom: 24px;
`

const Header = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '4px 0px 44px;' : '16px 0px')};
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.primary : props.theme.colors.cardSecondary};
  margin-bottom: 16px;
`

const Content = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && `margin-top: -54px;`}
`

const Card = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
  border-radius: 8px;
  margin: 8px 24px;
`

const TitleContainer = styled(Card)`
  flex-direction: ${(props) => (props.theme.breakpoint !== 'big' ? 'column' : 'row')};
  align-items: center;
`

const Titles = styled(View)`
  ${(props) => (props.theme.breakpoint !== 'big' ? 'margin-bottom: 15px' : 'flex: 1')};
`

const Title = styled(Text)<{ center?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Headline : props.theme.fonts.Title3)};
  ${(props) => (props.center || props.theme.breakpoint !== 'big') && 'text-align: center'};
`

const SubTitle = styled(Text)<{ center?: boolean }>`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) => (props.center || props.theme.breakpoint !== 'big') && 'text-align: center'};
`

const ConformityContainer = styled(View)`
  margin: ${(props) => (props.theme.breakpoint === 'big' ? 0 : 2)}px
    ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 20)}px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const InfoBold = styled(Text)<{ color?: string }>`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.color || props.theme.colors.primaryText};
`

const CommentContainer = styled(View)`
  margin: 8px 0;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const Subs = styled(View)`
  margin-left: 16px;
`

const ItemHeader = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
`

const ItemContent = styled(View)<{ audited: boolean; isLast: boolean; open: boolean }>`
  border: 0 solid ${(props) => props.theme.colors.backgroundTertiary};
  border-bottom-width: ${(props) => (props.audited || (props.isLast && !props.open) ? 0 : 1)}px;
`

const ItemOpen = styled(View)`
  flex: 1;
  padding-left: 16px;
`

const IconContainer = styled(View)`
  width: 30px;
  align-items: center;
  justify-content: center;
`

const ItemText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const QuestionItem = styled(View)<{ last: boolean }>`
  border: 0 solid ${(props) => props.theme.colors.backgroundTertiary};
  border-bottom-width: ${(props) => (props.last ? 0 : 1)}px;
  padding-bottom: 8px;
  margin-bottom: 16px;
`

const QuestionInfo = styled(Text)<{ color?: string }>`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.color || props.theme.colors.secondaryText};
`

const Infos = styled(View)`
  flex: 1;
`

const Line = styled(View)`
  flex-direction: row;
  margin-top: 16px;
`

const QuestionIcon = styled(View)`
  padding: 0 10px;
`

const Separator = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundTertiary};
  height: 1px;
  margin-top: 16px;
`

const Locations = styled(View)`
  margin: 8px 0 -${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
`
